/**
 * Point a research run at review pages we failed to find on our own.
 *
 * Automatic discovery follows links off the homepage, and guesses a
 * ProductReview slug from the brand name. Both miss in ways no rule
 * anticipates: Koala is listed as `koala-mattress`, which no cleaning of the
 * name "Koala" will ever produce, and its Google results were a wildlife
 * sanctuary, a fencing company and a different furniture retailer. Rather than
 * add a guess per brand, an admin pastes the page.
 *
 * Shared by the lead-generator queue and the research runner, because the
 * sources live on the SUBJECT and both screens act on subjects. The only thing
 * that differs is what "run it" means afterwards, which is the caller's job.
 */
import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Space, Typography, message } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    useSetHypothesisReviewSourcesMutation,
    useLazyGetHypothesisReviewSourcesQuery,
} from '../../../services/nestjs-base-api';

interface PastedBlock {
    label: string;
    text: string;
}

interface Props {
    jwt: string;
    /** Null closes the modal. */
    subjectId: number | null;
    /** Shown in the title, purely so the admin knows what they are editing. */
    brandName?: string | null;
    onClose: () => void;
    /**
     * Called after a successful save. Saving alone changes nothing until the
     * research runs again, so the caller re-runs however it re-runs — the
     * queue approves the lead, the runner triggers full research.
     */
    onSaved: () => Promise<void> | void;
}

const MAX_SOURCES = 5;

export const ReviewSourcesModal: React.FC<Props> = ({
    jwt,
    subjectId,
    brandName,
    onClose,
    onSaved,
}) => {
    const intl = useIntl();
    const [urlsText, setUrlsText] = useState('');
    const [blocks, setBlocks] = useState<PastedBlock[]>([{ label: '', text: '' }]);
    const [saving, setSaving] = useState(false);
    const [setReviewSources] = useSetHypothesisReviewSourcesMutation();
    const [loadSources, loadState] = useLazyGetHypothesisReviewSourcesQuery();

    // Show what is already attached before offering to replace it. Opening
    // empty and saving REPLACED whatever was stored, so pressing save just to
    // add a URL would silently erase a paste someone had made earlier.
    useEffect(() => {
        if (!subjectId) return;
        setUrlsText('');
        setBlocks([{ label: '', text: '' }]);
        loadSources({ jwtToken: jwt, subjectId })
            .unwrap()
            .then((current) => {
                setUrlsText((current.urls || []).join('\n'));
                setBlocks(
                    current.pasted?.length
                        ? current.pasted
                        : [{ label: '', text: '' }],
                );
            })
            .catch(() => undefined);
    }, [subjectId, jwt, loadSources]);

    const updateBlock = (i: number, patch: Partial<PastedBlock>) =>
        setBlocks((all) => all.map((b, n) => (n === i ? { ...b, ...patch } : b)));

    const save = async () => {
        if (!subjectId) return;
        setSaving(true);
        try {
            await setReviewSources({
                jwtToken: jwt,
                subjectId,
                urls: urlsText
                    .split(/[\s,]+/)
                    .map((u) => u.trim())
                    .filter(Boolean),
                pasted: blocks
                    .filter((b) => b.text.trim())
                    .map((b) => ({ label: b.label.trim() || 'Pasted', text: b.text })),
            }).unwrap();
            await onSaved();
            onClose();
        } catch {
            message.error(
                intl.formatMessage({
                    id: 'publicHypothesis.admin.reviewSourcesFailed',
                    defaultMessage: 'Could not save those pages.',
                }),
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={!!subjectId}
            width={720}
            title={intl.formatMessage(
                {
                    id: 'publicHypothesis.admin.reviewSourcesTitle',
                    defaultMessage: 'Review pages for {brand}',
                },
                { brand: brandName || '' },
            )}
            okText={intl.formatMessage({
                id: 'publicHypothesis.admin.reviewSourcesSave',
                defaultMessage: 'Save and run it',
            })}
            confirmLoading={saving || loadState.isFetching}
            onCancel={onClose}
            onOk={save}
        >
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
                <FormattedMessage
                    id="publicHypothesis.admin.reviewSourcesHelp"
                    defaultMessage="One URL per line. Any review site works: Trustpilot or Yelp for a US brand, ProductReview here, G2 for software. These are read before anything we look up ourselves."
                />
            </Typography.Paragraph>
            <Input.TextArea
                rows={4}
                value={urlsText}
                onChange={(e) => setUrlsText(e.target.value)}
                placeholder="https://www.productreview.com.au/listings/koala-mattress"
            />

            <Typography.Paragraph type="secondary" style={{ margin: '20px 0 12px' }}>
                <FormattedMessage
                    id="publicHypothesis.admin.pastedHelp"
                    defaultMessage="Or paste the page. Some platforms refuse us outright. Trustpilot answers 403 to anything that is not a browser, so copying the reviews out of your browser is the only way in. Paste the text, not a screenshot."
                />
            </Typography.Paragraph>

            {blocks.map((block, i) => (
                <div
                    key={i}
                    style={{
                        border: '1px solid #f0f0f0',
                        borderRadius: 8,
                        padding: 12,
                        marginBottom: 10,
                    }}
                >
                    <Space style={{ width: '100%', marginBottom: 8 }} align="center">
                        <Input
                            value={block.label}
                            onChange={(e) => updateBlock(i, { label: e.target.value })}
                            placeholder={intl.formatMessage({
                                id: 'publicHypothesis.admin.pastedLabelPlaceholder',
                                defaultMessage: 'Where it came from, e.g. Trustpilot',
                            })}
                            style={{ width: 320 }}
                        />
                        {blocks.length > 1 ? (
                            <Button
                                size="small"
                                type="text"
                                danger
                                onClick={() =>
                                    setBlocks((all) => all.filter((_, n) => n !== i))
                                }
                            >
                                <FormattedMessage
                                    id="publicHypothesis.admin.pastedRemove"
                                    defaultMessage="Remove"
                                />
                            </Button>
                        ) : null}
                    </Space>
                    <Input.TextArea
                        rows={5}
                        value={block.text}
                        onChange={(e) => updateBlock(i, { text: e.target.value })}
                        placeholder={intl.formatMessage({
                            id: 'publicHypothesis.admin.pastedPlaceholder',
                            defaultMessage: 'Paste the review page here. Site furniture and competitor names are fine to leave in.',
                        })}
                    />
                </div>
            ))}
            <Button
                size="small"
                onClick={() => setBlocks((all) => [...all, { label: '', text: '' }])}
                disabled={blocks.length >= MAX_SOURCES}
            >
                <FormattedMessage
                    id="publicHypothesis.admin.pastedAdd"
                    defaultMessage="Add another source"
                />
            </Button>
        </Modal>
    );
};
